import React, { useState, useContext } from 'react';
import styles from './styles.module.scss';
import Input, { Label } from 'src/components/Input';
import { useEffect } from 'react';
import { Provider } from 'src/stores/ProviderStore';
import {
    IProviderDetails,
    IProviderImmunization,
    IProviderLicensure,
    IProviderCertification,
} from 'common/lib/model/provider/providerDetails';
import { isValidNpi } from 'common/lib/util/ProviderUtil';
import { observer } from 'mobx-react';
import { OptionTypes } from '../OptionDropDown';
import Flex from '../Flex';
import NemsisOptionDropDown from '../NemsisOptionDropDown';
import { getDropdownValue } from 'src/utils/nemsis';
import { NaValues } from 'common/lib/model/form/naValues';
import SideModal from '../SideModal';
import ModalHeader from '../ModalHeader';
import ModalFooter from '../ModalFooter';
import * as providerService from 'src/api/providers';
import { ToastStoreObject } from 'src/stores/ToastStore';
import * as utils from 'src/utils';
import ReferenceListStore, { SelectComponent } from 'src/stores/ReferenceListStore';
import FacilityDropDown from '../FacilityDropDown';
import { Facility } from 'src/stores/FacilityStore';

enum DataTarget {
    PROVIDER = 'provider',
    PROVIDER_DETAIL = 'providerDetails',
}

export interface ProviderModalProps {
    provider: Provider;
    onSave: Function;
    onClose: Function;
    facilityId: Provider['facId'];
}

const defaultProviderImmunization: IProviderImmunization = {
    statusCode: '',
    year: null,
};

const defaultProviderLicensure: IProviderLicensure = {
    stateCode: '',
    licensureId: '',
    licensureLevelCode: '',
    certificationDate: null,
    initialStateLicensureIssueDate: null,
    currentStateLicensureExpirationDate: null,
};

const defaultProviderCertification: IProviderCertification = {
    practiceLevelCode: '',
    dateOfCertification: null,
};

const defaultNotValues: IProviderDetails['notValues'] = {
    provider: {
        phoneNumbers: [{}],
        emailAddresses: [{}],
    },
    providerDetails: {
        licensures: [{}],
    },
};

const defaultProviderDetails: IProviderDetails = {
    raceCodes: [],
    citizenshipCode: '',
    highestEducationalDegreeCode: '',
    fieldOfStudyCodes: [],
    motorVehicleLicenseTypeCodes: [],
    immunizations: [{ ...defaultProviderImmunization }],
    foreignLanguageAbilityCodes: [],
    licensures: [{ ...defaultProviderLicensure }],
    nationalRegistryNumber: '',
    nationalRegistryCertificationLevelCode: '',
    nationalRegistryExpirationDate: null,
    employmentStatusCode: '',
    employmentStatusDate: null,
    hireDate: null,
    primaryEmsJobRoleCode: '',
    otherJobResponsibilityCodes: [],
    yearsInService: null,
    yearsInServiceDocumentedDate: null,
    certifications: [{ ...defaultProviderCertification }],
    notValues: { ...defaultNotValues },
};

const defaultProvider: Provider = {
    prvrTyp: '',
    frstNm: '',
    lastNm: '',
    natlPrvrId: '',
    localPrvrId: '',
    spclty: '',
    groupNm: '',
    prvrDetails: utils.clone(defaultProviderDetails),
    middleName: '',
    addressLine1: '',
    addressLine2: '',
    cityCode: '',
    cityName: '',
    stateCode: '',
    zipCode: '',
    countryCode: '',
    phoneNumbers: [],
    emailAddresses: [],
    providerDob: null,
    genderCode: '',
};

function ProviderModal(props: ProviderModalProps) {
    const [data, setData] = useState<Provider>(
        props.provider
            ? { ...props.provider, prvrDetails: props.provider.prvrDetails || utils.clone(defaultProviderDetails) }
            : {
                  ...utils.clone(defaultProvider),
                  facId: props.facilityId,
              },
    );
    const isEdit = !!props.provider;
    const [hasValidNpi, setNpiWarning] = useState(
        (props.provider && props.provider.natlPrvrId && isValidNpi(props.provider.natlPrvrId)) || false,
    );
    const referenceListStore = useContext(ReferenceListStore);
    useEffect(() => {
        if (data.stateCode === '' || data.stateCode === null) return;
        referenceListStore.getFilteredData(OptionTypes.ANSICityCodes, {
            filterName: 'stateAnsiCode',
            filterValue: data.stateCode,
        });
    }, [data.stateCode]);

    const checkNpId = (value: string) => {
        setNpiWarning(isValidNpi(value));
        setData({ ...data, natlPrvrId: value });
    };

    function handleNaChange(target: DataTarget, property: string, subProperty?: string, index?: number) {
        const newData: any = { ...data };
        if (!data.prvrDetails) newData.prvrDetails = { ...defaultProviderDetails };

        if (!data.prvrDetails.notValues) {
            newData.prvrDetails.notValues = { ...defaultNotValues };
        }

        if (subProperty) {
            // Make sure everything is defaulted all the way down
            if (!newData.prvrDetails.notValues[target][property]) {
                newData.prvrDetails.notValues[target] = { ...newData.prvrDetails.notValues[target], [property]: [] };
            }
            if (!newData.prvrDetails.notValues[target][property][index]) {
                newData.prvrDetails.notValues[target][property][index] = {};
            }

            newData.prvrDetails.notValues[target][property][index][subProperty] = newData.prvrDetails.notValues[target][
                property
            ][index][subProperty]
                ? undefined
                : NaValues.NOT_APPLICABLE;
        } else {
            newData.prvrDetails.notValues[target][property] =
                newData.prvrDetails.notValues[target] && newData.prvrDetails.notValues[target][property]
                    ? undefined
                    : NaValues.NOT_APPLICABLE;
        }

        setData(newData);
    }

    const updateInput = (param: keyof Provider) => (t: string) => setData({ ...data, [param]: t });
    function updateNestedInput({
        param,
        subParam,
        index,
        value,
    }: {
        param: keyof Provider;
        subParam: string;
        index: number;
        value: any;
    }) {
        const newData: any = { ...data };
        if (!newData[param]) {
            newData[param] = [];
        }
        if (!newData[param][index]) {
            newData[param][index] = {};
        }
        newData[param][index][subParam] = value;
        setData(newData);
    }

    function getNotValBool(
        parentProp: string,
        childProp?: string,
        index?: number,
        target: DataTarget = DataTarget.PROVIDER,
    ) {
        if (target === DataTarget.PROVIDER)
            return getProviderNotValBool(parentProp as keyof Provider, childProp, index);
        else return getDetailsNotValBool(parentProp as keyof IProviderDetails, childProp, index);
    }

    function getProviderNotValBool(parentProp: keyof Provider, childProp?: string, index?: number) {
        if (childProp) {
            return !!(
                data &&
                data.prvrDetails &&
                data.prvrDetails.notValues &&
                data.prvrDetails.notValues.provider &&
                // @ts-ignore
                data.prvrDetails.notValues.provider[parentProp] &&
                // @ts-ignore
                data.prvrDetails.notValues.provider[parentProp][index] &&
                // @ts-ignore
                data.prvrDetails.notValues.provider[parentProp][index][childProp]
            );
        }
        return !!(
            data &&
            data.prvrDetails &&
            data.prvrDetails.notValues &&
            data.prvrDetails.notValues.provider &&
            // @ts-ignore
            data.prvrDetails.notValues.provider[parentProp]
        );
    }

    function getDetailsNotValBool(parentProp: keyof IProviderDetails, childProp?: string, index?: number) {
        if (childProp) {
            return !!(
                data &&
                data.prvrDetails &&
                data.prvrDetails.notValues &&
                data.prvrDetails.notValues.providerDetails &&
                data.prvrDetails.notValues.providerDetails[parentProp] &&
                data.prvrDetails.notValues.providerDetails[parentProp][index] &&
                data.prvrDetails.notValues.providerDetails[parentProp][index][childProp]
            );
        }
        return !!(
            data &&
            data.prvrDetails &&
            data.prvrDetails.notValues &&
            data.prvrDetails.notValues.providerDetails &&
            data.prvrDetails.notValues.providerDetails[parentProp]
        );
    }

    function renderDropDown({
        key,
        label,
        type,
        isMulti,
        isNa,
        nested,
        target = DataTarget.PROVIDER,
        virtualize = false,
        options,
        disabled,
    }: {
        key: keyof Provider;
        label: string;
        type: OptionTypes;
        isMulti?: boolean;
        isNa?: boolean;
        nested?: { index: number; row: any; key: string };
        target?: DataTarget;
        virtualize?: boolean;
        options?: SelectComponent[];
        disabled?: boolean;
    }) {
        const value = nested ? nested.row[nested.key] : data[key];
        return (
            <NemsisOptionDropDown
                nemsisLabel={label}
                isMulti={isMulti}
                selectedValue={
                    getNotValBool(key, nested && nested.key, nested && nested.index) ? NaValues.NOT_APPLICABLE : value
                }
                onChange={(d: any) => {
                    const val = getDropdownValue(d, isMulti ? [] : undefined);
                    if (nested) {
                        return updateNestedInput({ param: key, subParam: nested.key, index: nested.index, value: val });
                    }
                    return setData({ ...data, [key]: val });
                }}
                {...(isNa
                    ? {
                          onNaClick: () => handleNaChange(target, key, nested && nested.key, nested && nested.index),
                          naSelected: getNotValBool(key, nested && nested.key, nested && nested.index, target),
                      }
                    : {})}
                type={type}
                virtualize={virtualize}
                isDisabled={disabled}
                options={options || null}
            />
        );
    }

    async function handleSave() {
        try {
            if (isEdit) await providerService.editProvider(props.facilityId, data.prvrId, data);
            else await providerService.createProvider(props.facilityId, data);

            props.onSave();
            props.onClose();
        } catch (error) {
            ToastStoreObject.show(utils.parseError(error));
        }
    }

    function getPrimaryForm() {
        return (
            <>
                <Flex direction="row" justify="between" className={styles.formRow}>
                    <Flex value={1}>
                        <Input label="First Name" value={data.frstNm} onChangeText={updateInput('frstNm')} />
                    </Flex>
                    <Flex value={0.1} />
                    <Flex value={1}>
                        <Input label="Last Name" value={data.lastNm} onChangeText={updateInput('lastNm')} />
                    </Flex>
                </Flex>
                <Flex direction="row" justify="between" className={styles.formRow}>
                    <Flex value={1}>
                        {renderDropDown({
                            key: 'prvrTyp',
                            label: 'Provider Type',
                            type: OptionTypes.ProviderTypes,
                        })}
                    </Flex>
                </Flex>
                <Flex direction="row" justify="between" className={styles.formRow}>
                    <Flex value={1}>
                        <Input
                            label="NPI"
                            value={data.natlPrvrId}
                            infoState={!hasValidNpi ? 'warning' : null}
                            onChangeText={(value: string) => checkNpId(value)}
                        />
                    </Flex>
                    <Flex value={0.1}></Flex>
                    <Flex value={1}>
                        <Input
                            label="Local Provider Id"
                            value={data.localPrvrId}
                            onChangeText={updateInput('localPrvrId')}
                        />
                    </Flex>
                </Flex>
                <Flex direction="row" justify="between" className={styles.formRow}>
                    <Flex value={1}>
                        <Input label="Specialty" value={data.spclty} onChangeText={updateInput('spclty')} />
                    </Flex>
                </Flex>
                {!isEdit && (
                    <Flex direction="row" justify="between" className={styles.formRow}>
                        <Flex value={1}>
                            <Label text="Facility/s" />
                            <FacilityDropDown
                                selectedValue={props.facilityId}
                                isMulti={true}
                                onChange={(v: Facility[]) =>
                                    setData({ ...data, facilityList: v.map((f: any) => f.value) })
                                }
                            />
                        </Flex>
                    </Flex>
                )}
            </>
        );
    }

    return (
        <SideModal isOpen={true} onClose={() => props.onClose()}>
            <ModalHeader
                title={
                    isEdit
                        ? `Edit ${data && data.frstNm ? `${data.frstNm} ${data.lastNm}` : 'Provider'}`
                        : 'New Provider'
                }
                onClose={() => props.onClose()}
            />
            <div className={styles.contentWrap}>
                <Flex direction="column" className={styles.form}>
                    {getPrimaryForm()}

                </Flex>
            </div>
            <ModalFooter
                primaryText={isEdit ? 'Save' : 'Create'}
                primaryClick={handleSave}
                secondaryClick={() => {
                    setData(defaultProvider);
                    props.onClose();
                }}
            />
        </SideModal>
    );
}

export default observer(ProviderModal);
